import React, { useState, Fragment } from 'react';
import { EditIcon, TrashIcon, EyeIcon, XCircleIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon, BellIcon, SettingsIcon, RefreshCwIcon, ClipboardIcon } from 'lucide-react';
import { Message } from './MessagesManagement';
import { StatusBadge } from '../../StatusBadge';
interface MessagesListProps {
  messages: Message[];
  canEditMessage: (message: Message) => boolean;
  onEdit: (message: Message) => void;
  onDelete: (id: string) => void;
  onResetOverride: (id: string) => void;
  locale: string;
}
export function MessagesList({
  messages,
  canEditMessage,
  onEdit,
  onDelete,
  onResetOverride,
  locale
}: MessagesListProps) {
  const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getTypeIcon = (type: Message['type']) => {
    switch (type) {
      case 'Error':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-18874" />;
      case 'Warning':
        return <AlertTriangleIcon className="h-5 w-5 text-amber-500" data-id="element-18875" />;
      case 'Success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-18876" />;
      case 'Info':
        return <InfoIcon className="h-5 w-5 text-blue-500" data-id="element-18877" />;
      case 'Notification':
        return <BellIcon className="h-5 w-5 text-purple-500" data-id="element-18878" />;
      case 'System':
        return <SettingsIcon className="h-5 w-5 text-gray-500" data-id="element-18879" />;
      default:
        return null;
    }
  };
  const getTypeColor = (type: Message['type']) => {
    switch (type) {
      case 'Error':
        return 'text-red-700 bg-red-50';
      case 'Warning':
        return 'text-amber-700 bg-amber-50';
      case 'Success':
        return 'text-green-700 bg-green-50';
      case 'Info':
        return 'text-blue-700 bg-blue-50';
      case 'Notification':
        return 'text-purple-700 bg-purple-50';
      case 'System':
        return 'text-gray-700 bg-gray-50';
      default:
        return 'text-gray-700 bg-gray-50';
    }
  };
  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      // Could show a toast notification here
      console.log(`Copied ${key} to clipboard`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };
  const toggleExpand = (id: string) => {
    if (expandedMessage === id) {
      setExpandedMessage(null);
    } else {
      setExpandedMessage(id);
    }
  };
  if (messages.length === 0) {
    return <div className="text-center py-12" data-id="element-18880">
        <InfoIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-18881" />
        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-18882">
          No messages found
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-18883">
          No messages match your current filters. Try adjusting your search
          criteria.
        </p>
      </div>;
  }
  return <div className="overflow-x-auto" data-id="element-18884">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-18885">
        <thead data-id="element-18886">
          <tr data-id="element-18887">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18888">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18889">
              Key & Message
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18890">
              Module
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18891">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18892">
              Usage
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18893">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-18894">
          {messages.map(message => <Fragment key={message.id} data-id="element-18895">
              <tr className={expandedMessage === message.id ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-18896">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-18897">
                  <div className="flex items-center" data-id="element-18898">
                    <span className="flex-shrink-0" data-id="element-18899">
                      {getTypeIcon(message.type)}
                    </span>
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-18900">
                      {message.type}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-18901">
                  <div data-id="element-18902">
                    <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-18903">
                      <button onClick={() => handleCopyKey(message.key)} className="text-gray-500 hover:text-gray-700 mr-1" title="Copy key" data-id="element-18904">
                        <ClipboardIcon className="h-4 w-4" data-id="element-18905" />
                      </button>
                      <span className="font-mono" data-id="element-18906">{message.key}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500 line-clamp-2" data-id="element-18907">
                      {message.overriddenMessage ? <span className="text-indigo-600 font-medium" data-id="element-18908">
                          {message.overriddenMessage}
                        </span> : message.defaultMessage}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-18909">
                  <div className="text-sm text-gray-900" data-id="element-18910">{message.module}</div>
                  {message.context && <div className="text-sm text-gray-500" data-id="element-18911">
                      {message.context}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-18912">
                  <div className="flex flex-col space-y-1" data-id="element-18913">
                    <StatusBadge status={message.isActive ? 'success' : 'error'} text={message.isActive ? 'Active' : 'Inactive'} data-id="element-18914" />
                    {message.isSystemLocked && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-18915">
                        System Locked
                      </span>}
                    {message.overriddenMessage && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-18916">
                        Overridden
                      </span>}
                    {message.editableByRoles.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-18917">
                        RBAC Limited
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-18918">
                  <div data-id="element-18919">Count: {message.usageCount}</div>
                  <div data-id="element-18920">Last: {formatDate(message.lastUsedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-18921">
                  <div className="flex justify-end space-x-2" data-id="element-18922">
                    <button onClick={() => toggleExpand(message.id)} className="text-indigo-600 hover:text-indigo-900" title="View details" data-id="element-18923">
                      <EyeIcon className="h-5 w-5" data-id="element-18924" />
                    </button>
                    {canEditMessage(message) && <>
                        <button onClick={() => onEdit(message)} className="text-indigo-600 hover:text-indigo-900" title="Edit message" data-id="element-18925">
                          <EditIcon className="h-5 w-5" data-id="element-18926" />
                        </button>
                        {message.overriddenMessage && <button onClick={() => onResetOverride(message.id)} className="text-amber-600 hover:text-amber-900" title="Reset override" data-id="element-18927">
                            <RefreshCwIcon className="h-5 w-5" data-id="element-18928" />
                          </button>}
                        {!message.isSystemLocked && <button onClick={() => onDelete(message.id)} className="text-red-600 hover:text-red-900" title="Delete message" data-id="element-18929">
                            <TrashIcon className="h-5 w-5" data-id="element-18930" />
                          </button>}
                      </>}
                  </div>
                </td>
              </tr>
              {expandedMessage === message.id && <tr className="bg-gray-50" data-id="element-18931">
                  <td colSpan={6} className="px-6 py-4" data-id="element-18932">
                    <div className="space-y-4" data-id="element-18933">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-18934">
                        <div data-id="element-18935">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18936">
                            Default Message
                          </h4>
                          <div className={`p-3 rounded-md text-sm ${getTypeColor(message.type)}`} data-id="element-18937">
                            {message.defaultMessage}
                          </div>
                        </div>
                        {message.overriddenMessage && <div data-id="element-18938">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18939">
                              Overridden Message
                            </h4>
                            <div className={`p-3 rounded-md text-sm ${getTypeColor(message.type)}`} data-id="element-18940">
                              {message.overriddenMessage}
                            </div>
                          </div>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-18941">
                        <div data-id="element-18942">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18943">
                            Created
                          </h4>
                          <div className="text-sm text-gray-500" data-id="element-18944">
                            <div data-id="element-18945">{formatDate(message.createdAt)}</div>
                            <div data-id="element-18946">By: {message.createdBy}</div>
                          </div>
                        </div>
                        {message.modifiedAt && <div data-id="element-18947">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18948">
                              Last Modified
                            </h4>
                            <div className="text-sm text-gray-500" data-id="element-18949">
                              <div data-id="element-18950">{formatDate(message.modifiedAt)}</div>
                              <div data-id="element-18951">By: {message.modifiedBy}</div>
                            </div>
                          </div>}
                        <div data-id="element-18952">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18953">
                            RBAC Permissions
                          </h4>
                          {message.editableByRoles.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-18954">
                              {message.editableByRoles.map(role => <span key={role} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-18955">
                                  {role}
                                </span>)}
                            </div> : <div className="text-sm text-gray-500" data-id="element-18956">
                              {message.isSystemLocked ? 'System locked - no editing allowed' : 'No RBAC restrictions'}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>}
            </Fragment>)}
        </tbody>
      </table>
    </div>;
}